// Composant Analyse HALTES Consolidee
// Affiche les 5 axes avec progression globale et identification du metier le plus faible

import { useMemo } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '../ui'

// Configuration des axes HALTES
const HALTES_AXES_CONFIG = [
  { key: 'H', label: 'Hygiene', color: '#2D8C3C', bgColor: 'bg-primary/5', textColor: 'text-primary' },
  { key: 'A', label: 'Acces', color: '#2D8C3C', bgColor: 'bg-primary/5', textColor: 'text-primary' },
  { key: 'LT', label: 'Livraisons / Taches', color: '#2D8C3C', bgColor: 'bg-primary/5', textColor: 'text-primary' },
  { key: 'E', label: 'Energies', color: '#2D8C3C', bgColor: 'bg-primary/5', textColor: 'text-primary' },
  { key: 'S', label: 'Secours', color: '#2D8C3C', bgColor: 'bg-primary/5', textColor: 'text-primary' },
] as const

export type HaltesAxe = 'H' | 'A' | 'LT' | 'E' | 'S'

export interface MetierScoreData {
  metierId: string
  metierNom: string
  scores: Record<HaltesAxe, number>
  maxScores: Record<HaltesAxe, number>
}

export interface HaltesConsolidatedProps {
  metiersData: MetierScoreData[]
  compact?: boolean
}

interface ConsolidatedAxeData {
  axe: HaltesAxe
  label: string
  color: string
  bgColor: string
  textColor: string
  totalScore: number
  totalMax: number
  percent: number
  lowestMetier: {
    metierId: string
    metierNom: string
    score: number
    max: number
    percent: number
  } | null
}

export default function HaltesConsolidatedAnalysis({
  metiersData,
  compact = false,
}: HaltesConsolidatedProps) {
  // Calculer les donnees consolidees par axe
  const consolidatedData = useMemo((): ConsolidatedAxeData[] => {
    return HALTES_AXES_CONFIG.map((axeConfig) => {
      const axe = axeConfig.key as HaltesAxe

      // Consolider les scores de tous les metiers pour cet axe
      let totalScore = 0
      let totalMax = 0
      let lowestMetier: ConsolidatedAxeData['lowestMetier'] = null
      let lowestPercent = Infinity

      metiersData.forEach((metier) => {
        const score = metier.scores[axe] || 0
        const max = metier.maxScores[axe] || 0

        totalScore += score
        totalMax += max

        // Identifier le metier le plus faible
        if (max > 0) {
          const percent = (score / max) * 100
          if (percent < lowestPercent) {
            lowestPercent = percent
            lowestMetier = {
              metierId: metier.metierId,
              metierNom: metier.metierNom,
              score,
              max,
              percent,
            }
          }
        }
      })

      const percent = totalMax > 0 ? (totalScore / totalMax) * 100 : 0

      return {
        axe,
        label: axeConfig.label,
        color: axeConfig.color,
        bgColor: axeConfig.bgColor,
        textColor: axeConfig.textColor,
        totalScore,
        totalMax,
        percent,
        lowestMetier,
      }
    })
  }, [metiersData])

  // Determiner la couleur de progression
  const getProgressColor = (percent: number) => {
    if (percent >= 70) return 'bg-green-500'
    if (percent >= 40) return 'bg-orange-500'
    return 'bg-red-500'
  }

  const getStatusLabel = (percent: number, isNA: boolean) => {
    if (isNA) return { text: 'N/A', color: 'text-gray-400' }
    if (percent >= 70) return { text: 'Bon', color: 'text-green-600' }
    if (percent >= 40) return { text: 'A ameliorer', color: 'text-orange-600' }
    return { text: 'Insuffisant', color: 'text-red-600' }
  }

  if (compact) {
    return (
      <div className="grid grid-cols-5 gap-2">
        {consolidatedData.map((data) => {
          const isNotApplicable = data.totalMax === 0
          const status = getStatusLabel(data.percent, isNotApplicable)
          return (
            <div
              key={data.axe}
              className={`bg-gray-50 border border-gray-100 rounded-lg p-2 text-center ${isNotApplicable ? 'opacity-50' : ''}`}
            >
              <div className={`text-xs font-bold ${isNotApplicable ? 'text-gray-400' : 'text-gray-600'}`}>{data.axe}</div>
              <div className={`text-lg font-bold ${isNotApplicable ? 'text-gray-400' : 'text-gray-900'}`}>
                {isNotApplicable ? '—' : `${Math.round(data.percent)}%`}
              </div>
              <div className={`text-xs font-medium ${status.color}`}>{status.text}</div>
            </div>
          )
        })}
      </div>
    )
  }

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-lg font-semibold flex items-center gap-2">
          <svg className="w-5 h-5 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
          </svg>
          Analyse HALTES - Tous Metiers Confondus
        </CardTitle>
        <p className="text-sm text-gray-500">
          Niveau d'avancement par theme avec identification des points faibles
        </p>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          {consolidatedData.map((data) => {
            const isNotApplicable = data.totalMax === 0
            const status = getStatusLabel(data.percent, isNotApplicable)
            const isAlert = !isNotApplicable && data.percent < 50

            return (
              <div
                key={data.axe}
                className={`p-3 rounded-lg bg-gray-50 border border-gray-100 ${isNotApplicable ? 'opacity-50' : ''}`}
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-3">
                    {/* Badge lettre sobre en gris */}
                    <span
                      className={`w-8 h-8 rounded-lg flex items-center justify-center font-bold text-sm ${isNotApplicable ? 'bg-gray-200 text-gray-400' : 'bg-gray-200 text-gray-600'}`}
                    >
                      {data.axe}
                    </span>
                    <div>
                      <span className={`font-medium ${isNotApplicable ? 'text-gray-400 italic' : 'text-gray-700'}`}>
                        {data.label}
                      </span>
                      {/* Étiquette statut colorée */}
                      <span className={`ml-2 text-sm font-medium ${status.color}`}>
                        {status.text}
                      </span>
                    </div>
                  </div>
                  <div className="text-right">
                    <span className={`text-xl font-bold ${isNotApplicable ? 'text-gray-400' : 'text-gray-900'}`}>
                      {isNotApplicable ? '—' : `${Math.round(data.percent)}%`}
                    </span>
                    {!isNotApplicable && (
                      <span className="text-sm text-gray-500 ml-1">
                        ({data.totalScore}/{data.totalMax})
                      </span>
                    )}
                  </div>
                </div>

                {/* Barre de progression colorée */}
                <div className="h-2.5 bg-gray-200 rounded-full overflow-hidden mb-2">
                  {!isNotApplicable && (
                    <div
                      className={`h-full ${getProgressColor(data.percent)} transition-all duration-500`}
                      style={{ width: `${Math.min(100, data.percent)}%` }}
                    />
                  )}
                </div>

                {/* Metier le plus faible - masqué si N/A */}
                {!isNotApplicable && data.lowestMetier && metiersData.length > 1 && (
                  <div className={`flex items-center gap-2 text-sm ${isAlert ? 'text-red-600 font-medium' : 'text-gray-500'}`}>
                    {isAlert && (
                      <svg className="w-4 h-4 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                      </svg>
                    )}
                    <span>
                      Point faible : <strong>{data.lowestMetier.metierNom}</strong>
                      {' '}({Math.round(data.lowestMetier.percent)}%)
                    </span>
                  </div>
                )}
              </div>
            )
          })}
        </div>

        {/* Resume global */}
        <div className="mt-4 pt-4 border-t border-gray-200">
          <div className="flex items-center justify-between text-sm">
            <span className="text-gray-600">Score total consolide :</span>
            <span className="font-bold text-gray-900">
              {consolidatedData.reduce((sum, d) => sum + d.totalScore, 0)}
              /
              {consolidatedData.reduce((sum, d) => sum + d.totalMax, 0)} pts
              {' '}
              ({Math.round(
                (consolidatedData.reduce((sum, d) => sum + d.totalScore, 0) /
                  Math.max(1, consolidatedData.reduce((sum, d) => sum + d.totalMax, 0))) * 100
              )}%)
            </span>
          </div>
        </div>
      </CardContent>
    </Card>
  )
}

// Export des types pour reutilisation
export { HALTES_AXES_CONFIG }
